const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About GJC",
      solutions: "Solutions",
      markets: "Markets",
      whyGJC: "Why GJC",
      contact: "Contact",
      talkToUs: "Discuss a Project",
    },
    hero: {
      overline: "Energy Infrastructure · BESS · Digital Power · Chile & LATAM",
      headline: "Energy Infrastructure, BESS & Digital Power Solutions for Chile and LATAM",
      electronToBit: "From the electron to the bit",
      subheadline: "We integrate energy, critical digital infrastructure and business strategy to transform complex opportunities into executable, bankable and profitable projects.",
      cta1: "Discuss a Project",
      cta2: "Explore Solutions",
    },
    coreBusiness: {
      overline: "Our Core Value",
      title: "Energy, critical digital infrastructure and business strategy, integrated for execution",
      text: "GJC integrates energy, critical digital infrastructure and business strategy to transform complex opportunities into executable, bankable and profitable projects.",
      details: "Our work covers BESS integration, electrical infrastructure, telecom energy, AI/data center energy architecture and strategic market-entry support for international technology partners.",
    },
    partners: {
      overline: "Technology Partners",
      title: "Local execution support for international technology companies",
      tianneng: {
        badge: "Exclusive Partner (Chile + LATAM | Telecom & Battery/BESS)",
        description: "Tianneng is an established energy storage and battery technology group with a global footprint, supplying telecom-grade lithium solutions and broader energy storage technologies.",
        microcopy: "GJC supports international technology partners with local market access, project origination, stakeholder mapping and technical-commercial structuring.",
        visitWebsite: "Visit website",
        disclaimer: "GJC takes a best-fit approach for integration and electrical infrastructure, selecting the architecture aligned with client standards, grid-code compliance and project-specific bankability. In Chile and LATAM, GJC has an exclusive partnership agreement with Tianneng for telecom energy and battery/BESS-based solutions within that scope.",
      },
    },
    proofPoints: {
      title: "Why clients and partners engage GJC",
      items: [
        { title: "Bankability", desc: "We structure projects in a way that investors, lenders, utilities and strategic partners can evaluate and support." },
        { title: "Deployability", desc: "We focus on execution readiness: architecture, integration logic, procurement support and local requirements." },
        { title: "Local Access", desc: "We connect global technology with Chilean and LATAM utilities, miners, developers, industrial clients and infrastructure stakeholders." },
        { title: "Technical-Commercial Bridge", desc: "We translate engineering constraints into business decisions and commercial opportunities." },
      ],
    },
    servicesOverview: {
      overline: "Core Capabilities",
      title: "Four integrated lines of business",
      items: [
        { title: "BESS & Energy Storage", desc: "Utility-scale, C&I, mining and backup energy storage integration, including system architecture and technical-commercial advisory." },
        { title: "Electrical Infrastructure", desc: "Transformers, MV switchgear, cabling, connection architecture, grid-code support and integration with balance of plant requirements." },
        { title: "Digital Power & AI Infrastructure", desc: "Energy architecture for data centers, telecom networks, critical infrastructure and AI-ready power systems." },
        { title: "Strategic Advisory & Market Entry", desc: "Commercial development, stakeholder mapping, supplier representation, project origination and partnership structuring in Chile and LATAM." },
        { title: "Telecom Energy Solutions", desc: "Lithium backup systems, remote site energy, hybrid concepts and distributed energy architectures for telecom and critical networks." },
      ],
    },
    process: {
      overline: "Our Approach",
      title: "From strategy to execution readiness",
      steps: [
        { num: "01", title: "Strategic Positioning", desc: "We clarify the opportunity, client need, market context and bankability logic before proposing a solution." },
        { num: "02", title: "Technical-Commercial Architecture", desc: "We define the practical energy architecture: BESS, PCS, transformers, MV connection, cabling, controls and integration boundaries." },
        { num: "03", title: "Execution Readiness", desc: "We support procurement, stakeholder alignment, supplier coordination, due diligence and deployment preparation." },
      ],
    },
    chileBlock: {
      overline: "Chile & LATAM Opportunity",
      title: "Chile is becoming a strategic energy infrastructure hub",
      text: "Chile combines renewable generation, mining demand, grid congestion, industrial electrification and emerging AI/data center requirements. GJC operates at the intersection of these forces, connecting international technology with local execution capability across Chile and LATAM.",
    },
    leadership: {
      overline: "Leadership",
      title: "Senior commercial and technical experience behind GJC",
      cta: "Learn More About Us",
      members: [
        { name: "Luis Garcia", role: "Business VP LATAM", bio: "Senior commercial executive with experience in telecommunications, energy infrastructure, BESS, digital infrastructure and complex B2B development across Chile and Latin America." },
        { name: "Nicolas Vidal", role: "Design Director & CEO", bio: "Leads GJC's strategic vision and design approach, connecting technical design, commercial execution and project development requirements." },
        { name: "Francisco Cordero", role: "COO & CMO", bio: "Oversees operations, positioning and go-to-market execution for energy, infrastructure and technology partnerships." },
      ],
    },
    about: {
      overline: "About GJC Enertec",
      headline: "A boutique platform for energy infrastructure integration and strategic advisory",
      intro: "GJC Enertec Chile SpA connects manufacturers, investors, developers, utilities, miners, industrial clients, telecom operators and digital infrastructure stakeholders across Chile and Latin America.",
      mission: {
        title: "Our Core Value",
        text: "We integrate energy, critical digital infrastructure and business strategy to transform complex opportunities into executable, bankable and profitable projects.",
      },
      quote: "From the electron to the bit, GJC bridges energy, critical digital infrastructure and business strategy to create executable projects and long-term value.",
      values: [
        { title: "Technical Rigor", desc: "Every recommendation is grounded in engineering reality, grid requirements and project-specific constraints." },
        { title: "Commercial Clarity", desc: "We connect technical solutions with business cases that stakeholders can evaluate and commit to." },
        { title: "Local Market Access", desc: "Deep understanding of Chile's commercial, regulatory and operational landscape." },
        { title: "Technology Agnostic", desc: "We focus on what fits the project, the client and the bankability requirement." },
      ],
    },
    solutions: {
      overline: "Solutions",
      headline: "Structured energy solutions for complex infrastructure projects",
      categories: [
        {
          title: "BESS & Energy Storage Integration",
          desc: "Energy storage architecture and integration for utility-scale, mining, industrial and critical infrastructure applications.",
          items: ["Utility-scale and C&I BESS integration", "System architecture and sizing logic", "Technical-commercial advisory", "PCS, batteries, transformers, MV connection and balance of plant coordination"],
        },
        {
          title: "Electrical Infrastructure",
          desc: "Practical grid and electrical infrastructure support for bankable energy projects.",
          items: ["MV cells and switchgear", "Step-up transformers", "AC/DC cabling", "Connection architecture", "Grid-code compliant design support"],
        },
        {
          title: "Telecom & Critical Power",
          desc: "Energy architecture for telecom networks, remote operations and critical distributed infrastructure.",
          items: ["Lithium backup systems", "Remote site energy", "Hybrid power concepts", "Critical network resilience"],
        },
        {
          title: "AI & Data Center Energy Advisory",
          desc: "Energy infrastructure strategy for the next generation of digital assets.",
          items: ["Energy architecture for AI infrastructure", "BESS as enabling architecture", "Co-location logic with generation assets", "Power resilience and deployment readiness"],
        },
        {
          title: "Strategic Advisory & Market Entry",
          desc: "Commercial development and local market access for international technology companies entering Chile and LATAM.",
          items: ["Market-entry support", "Stakeholder mapping", "Project origination", "Partnership structuring", "Supplier representation"],
        },
      ],
    },
    markets: {
      overline: "Markets We Serve",
      headline: "Energy infrastructure across sectors",
      intro: "GJC works where energy, industry, mining and digital infrastructure converge — serving stakeholders who need reliable, bankable and executable energy infrastructure.",
      sectors: [
        { title: "Utilities & Generators", desc: "Grid-connected generation, BESS and energy infrastructure projects requiring integration architecture and stakeholder coordination." },
        { title: "Mining & Industrial Clients", desc: "Power resilience, energy optimization and electrification support for demanding industrial environments." },
        { title: "Telecom & Critical Infrastructure", desc: "Distributed energy, backup power, lithium systems and remote site energy architecture." },
        { title: "Data Centers & AI Infrastructure", desc: "Energy strategy, BESS-enabled resilience and power architecture for AI-ready facilities." },
        { title: "Investors & Developers", desc: "Project structuring, market intelligence, due diligence support and bankability alignment." },
        { title: "International OEMs", desc: "Local market access, representation, stakeholder mapping and commercial development in Chile and LATAM." },
        { title: "IPPs & Renewable Developers", desc: "Storage, grid connection and infrastructure strategy for renewable generation portfolios." },
        { title: "Strategic Partners", desc: "Structured collaboration between capital, technology and local execution capabilities." },
      ],
    },
    whyGJC: {
      overline: "Why GJC",
      headline: "A practical bridge between technology, capital and execution",
      intro: "GJC combines senior commercial experience, technical understanding and direct access to Chilean and LATAM energy infrastructure stakeholders. We focus on making opportunities executable, not just describing them.",
      differentiators: [
        { title: "Senior B2B Experience", desc: "Commercial experience across telecommunications, energy, mining, infrastructure and regional technology expansion." },
        { title: "Local Market Access", desc: "Relationships and understanding across utilities, miners, developers, telecom operators, suppliers and infrastructure stakeholders." },
        { title: "Technical-Commercial Integration", desc: "Ability to connect engineering, procurement, bankability and commercial strategy in one coherent project logic." },
        { title: "International Interface", desc: "A local partner for global manufacturers, investors and technology companies entering Chile and LATAM." },
        { title: "Execution Mindset", desc: "We focus on structured action, stakeholder alignment and project readiness rather than generic advisory output." },
        { title: "Technology Agnostic", desc: "We recommend what fits the project, not what fits a single brand relationship." },
        { title: "Bankability Focus", desc: "We help projects become understandable and actionable for capital providers, clients and strategic partners." },
      ],
    },
    contact: {
      overline: "Get in Touch",
      headline: "Let's discuss your energy infrastructure project",
      intro: "Whether you are developing a BESS project, entering Chile or LATAM with technology, evaluating a mining or industrial energy requirement, or structuring digital power infrastructure, GJC can help define the next step.",
      form: {
        name: "Full Name",
        company: "Company",
        email: "Email",
        interest: "Area of Interest",
        interestOptions: ["BESS Integration", "Electrical Infrastructure", "Telecom Energy", "AI & Data Center Energy", "Market Entry", "Strategic Advisory", "Partnership Opportunity", "Other"],
        message: "Message",
        submit: "Send Message",
        success: "Thank you. We'll be in touch shortly.",
      },
      whatsapp: "Chat on WhatsApp",
      email: "Email Us",
      emailAddress: "contacto@gjcenertec.com",
    },
    footer: {
      tagline: "Building bankable energy infrastructure across Chile and LATAM.",
      copyright: "GJC Enertec Chile SpA. All rights reserved.",
      nav: {
        company: "Company",
        solutions: "Solutions",
        connect: "Connect",
      },
    },
  },
  es: {
    nav: {
      home: "Inicio",
      about: "Nosotros",
      solutions: "Soluciones",
      markets: "Mercados",
      whyGJC: "Por qué GJC",
      contact: "Contacto",
      talkToUs: "Conversemos tu proyecto",
    },
    hero: {
      overline: "Infraestructura Energética · BESS · Digital Power · Chile & LATAM",
      headline: "Soluciones de Infraestructura Energética, BESS y Digital Power para Chile y LATAM",
      electronToBit: "Desde el electrón al bit",
      subheadline: "Integramos energía, infraestructura digital crítica y estrategia de negocio para transformar oportunidades complejas en proyectos ejecutables, bancables y rentables.",
      cta1: "Conversemos tu proyecto",
      cta2: "Explorar Soluciones",
    },
    coreBusiness: {
      overline: "Nuestro Core Value",
      title: "Energía, infraestructura digital crítica y estrategia de negocio, integradas para ejecutar",
      text: "Integramos energía, infraestructura digital crítica y estrategia de negocio para transformar oportunidades complejas en proyectos ejecutables, bancables y rentables.",
      details: "Nuestro trabajo cubre integración BESS, infraestructura eléctrica, energía telecom, arquitectura energética para data centers/IA y soporte estratégico de entrada al mercado para socios tecnológicos internacionales.",
    },
    partners: {
      overline: "Socios Tecnológicos",
      title: "Soporte local de ejecución para empresas tecnológicas internacionales",
      tianneng: {
        badge: "Partner Exclusivo (Chile + LATAM | Telco & Baterías/BESS)",
        description: "Tianneng es un grupo tecnológico consolidado en baterías y almacenamiento energético con presencia global, con foco en soluciones de litio para telecomunicaciones y tecnologías de almacenamiento de energía.",
        microcopy: "GJC apoya a socios tecnológicos internacionales con acceso local, originación de proyectos, mapeo de stakeholders y estructuración técnico-comercial.",
        visitWebsite: "Visitar sitio",
        disclaimer: "GJC mantiene un enfoque flexible para la integración y la infraestructura eléctrica, seleccionando la arquitectura que mejor se ajuste a los estándares del cliente, al cumplimiento normativo y a la bancabilidad del proyecto. En Chile y LATAM, GJC cuenta con un acuerdo de partnership exclusivo con Tianneng para energía en telecomunicaciones y soluciones basadas en baterías/BESS dentro de ese alcance.",
      },
    },
    proofPoints: {
      title: "Por qué clientes y partners trabajan con GJC",
      items: [
        { title: "Bancabilidad", desc: "Estructuramos proyectos para que inversionistas, financistas, utilities y socios estratégicos puedan evaluarlos y apoyarlos." },
        { title: "Desplegabilidad", desc: "Nos enfocamos en preparación de ejecución: arquitectura, integración, soporte de compra y requisitos locales." },
        { title: "Acceso Local", desc: "Conectamos tecnología global con utilities, mineras, desarrolladores, clientes industriales e infraestructura en Chile y LATAM." },
        { title: "Puente Técnico-Comercial", desc: "Traducimos restricciones de ingeniería en decisiones de negocio y oportunidades comerciales." },
      ],
    },
    servicesOverview: {
      overline: "Capacidades Principales",
      title: "Cuatro líneas de negocio integradas",
      items: [
        { title: "BESS y Almacenamiento", desc: "Integración de almacenamiento utility-scale, C&I, minería y respaldo, incluyendo arquitectura de sistemas y asesoría técnico-comercial." },
        { title: "Infraestructura Eléctrica", desc: "Transformadores, celdas MT, cableado, arquitectura de conexión, soporte de cumplimiento de red e integración con balance de planta." },
        { title: "Digital Power e Infraestructura IA", desc: "Arquitectura energética para data centers, redes telecom, infraestructura crítica y sistemas de potencia AI-ready." },
        { title: "Advisory Estratégico y Market Entry", desc: "Desarrollo comercial, mapeo de stakeholders, representación de proveedores, originación de proyectos y estructuración de alianzas en Chile y LATAM." },
        { title: "Soluciones de Energía Telecom", desc: "Sistemas de litio, respaldo energético, sitios remotos, soluciones híbridas y arquitecturas distribuidas para redes críticas." },
      ],
    },
    process: {
      overline: "Nuestro Enfoque",
      title: "De la estrategia a la preparación para ejecución",
      steps: [
        { num: "01", title: "Posicionamiento Estratégico", desc: "Clarificamos la oportunidad, la necesidad del cliente, el contexto de mercado y la lógica de bancabilidad antes de proponer una solución." },
        { num: "02", title: "Arquitectura Técnico-Comercial", desc: "Definimos la arquitectura energética práctica: BESS, PCS, transformadores, conexión MT, cableado, control e integración." },
        { num: "03", title: "Preparación para Ejecución", desc: "Apoyamos compras, alineación de stakeholders, coordinación de proveedores, due diligence y preparación de despliegue." },
      ],
    },
    chileBlock: {
      overline: "Oportunidad Chile & LATAM",
      title: "Chile se está convirtiendo en un hub estratégico de infraestructura energética",
      text: "Chile combina generación renovable, demanda minera, congestión de red, electrificación industrial y nuevos requerimientos de data centers e IA. GJC opera en la intersección de estas fuerzas, conectando tecnología internacional con capacidad local de ejecución en Chile y LATAM.",
    },
    leadership: {
      overline: "Liderazgo",
      title: "Experiencia senior comercial y técnica detrás de GJC",
      cta: "Conoce Más Sobre Nosotros",
      members: [
        { name: "Luis García", role: "Business VP LATAM", bio: "Ejecutivo comercial senior con experiencia en telecomunicaciones, infraestructura energética, BESS, infraestructura digital y desarrollo B2B complejo en Chile y Latinoamérica." },
        { name: "Nicolás Vidal", role: "Director de Diseño & CEO", bio: "Lidera la visión estratégica y el enfoque de diseño de GJC, conectando diseño técnico, ejecución comercial y requerimientos de desarrollo de proyectos." },
        { name: "Francisco Cordero", role: "COO & CMO", bio: "Supervisa operaciones, posicionamiento y ejecución go-to-market para alianzas de energía, infraestructura y tecnología." },
      ],
    },
    about: {
      overline: "Sobre GJC Enertec",
      headline: "Una plataforma boutique de integración y advisory en infraestructura energética",
      intro: "GJC Enertec Chile SpA conecta fabricantes, inversionistas, desarrolladores, utilities, mineras, clientes industriales, operadores telecom y actores de infraestructura digital en Chile y Latinoamérica.",
      mission: {
        title: "Nuestro Core Value",
        text: "Integramos energía, infraestructura digital crítica y estrategia de negocio para transformar oportunidades complejas en proyectos ejecutables, bancables y rentables.",
      },
      quote: "Desde el electrón al bit, GJC conecta energía, infraestructura digital crítica y estrategia de negocio para crear proyectos ejecutables y valor de largo plazo.",
      values: [
        { title: "Rigor Técnico", desc: "Cada recomendación se basa en realidad de ingeniería, requisitos de red y restricciones específicas del proyecto." },
        { title: "Claridad Comercial", desc: "Conectamos soluciones técnicas con casos de negocio que los stakeholders pueden evaluar y comprometer." },
        { title: "Acceso al Mercado Local", desc: "Profundo entendimiento del panorama comercial, regulatorio y operacional de Chile." },
        { title: "Agnósticos en Tecnología", desc: "Nos enfocamos en lo que calza con el proyecto, el cliente y la bancabilidad requerida." },
      ],
    },
    solutions: {
      overline: "Soluciones",
      headline: "Soluciones energéticas estructuradas para proyectos complejos de infraestructura",
      categories: [
        {
          title: "Integración BESS y Almacenamiento",
          desc: "Arquitectura e integración de almacenamiento para aplicaciones utility-scale, minería, industria e infraestructura crítica.",
          items: ["Integración BESS utility-scale y C&I", "Arquitectura y lógica de dimensionamiento", "Asesoría técnico-comercial", "Coordinación de PCS, baterías, transformadores, conexión MT y balance de planta"],
        },
        {
          title: "Infraestructura Eléctrica",
          desc: "Soporte práctico de red e infraestructura eléctrica para proyectos energéticos bancables.",
          items: ["Celdas MT y switchgear", "Transformadores elevadores", "Cableado AC/DC", "Arquitectura de conexión", "Soporte de diseño conforme a código de red"],
        },
        {
          title: "Telecom y Energía Crítica",
          desc: "Arquitectura energética para redes telecom, operaciones remotas e infraestructura distribuida crítica.",
          items: ["Sistemas de respaldo en litio", "Energía para sitios remotos", "Conceptos híbridos", "Resiliencia para redes críticas"],
        },
        {
          title: "Advisory Energético para IA y Data Centers",
          desc: "Estrategia de infraestructura energética para la próxima generación de activos digitales.",
          items: ["Arquitectura energética para infraestructura IA", "BESS como arquitectura habilitadora", "Lógica de co-ubicación con generación", "Resiliencia energética y preparación de despliegue"],
        },
        {
          title: "Advisory Estratégico y Market Entry",
          desc: "Desarrollo comercial y acceso local para empresas tecnológicas internacionales que ingresan a Chile y LATAM.",
          items: ["Soporte de entrada al mercado", "Mapeo de stakeholders", "Originación de proyectos", "Estructuración de alianzas", "Representación de proveedores"],
        },
      ],
    },
    markets: {
      overline: "Mercados que Servimos",
      headline: "Infraestructura energética entre sectores",
      intro: "GJC trabaja donde convergen energía, industria, minería e infraestructura digital, sirviendo a stakeholders que necesitan infraestructura energética confiable, bancable y ejecutable.",
      sectors: [
        { title: "Utilities y Generadores", desc: "Generación conectada a red, BESS e infraestructura energética que requiere arquitectura de integración y coordinación de stakeholders." },
        { title: "Minería e Industria", desc: "Resiliencia energética, optimización de potencia y soporte de electrificación para entornos industriales exigentes." },
        { title: "Telecom e Infraestructura Crítica", desc: "Energía distribuida, respaldo, sistemas de litio y arquitectura energética para sitios remotos." },
        { title: "Data Centers e Infraestructura IA", desc: "Estrategia energética, resiliencia habilitada por BESS y arquitectura de potencia para instalaciones AI-ready." },
        { title: "Inversionistas y Desarrolladores", desc: "Estructuración de proyectos, inteligencia de mercado, soporte de due diligence y alineación de bancabilidad." },
        { title: "OEMs Internacionales", desc: "Acceso local, representación, mapeo de stakeholders y desarrollo comercial en Chile y LATAM." },
        { title: "IPPs y Desarrolladores Renovables", desc: "Estrategia de almacenamiento, conexión a red e infraestructura para portafolios de generación renovable." },
        { title: "Socios Estratégicos", desc: "Colaboración estructurada entre capital, tecnología y capacidad local de ejecución." },
      ],
    },
    whyGJC: {
      overline: "Por qué GJC",
      headline: "Un puente práctico entre tecnología, capital y ejecución",
      intro: "GJC combina experiencia comercial senior, entendimiento técnico y acceso directo a stakeholders de infraestructura energética en Chile y LATAM. Nos enfocamos en hacer ejecutables las oportunidades, no solo en describirlas.",
      differentiators: [
        { title: "Experiencia B2B Senior", desc: "Experiencia comercial en telecomunicaciones, energía, minería, infraestructura y expansión tecnológica regional." },
        { title: "Acceso al Mercado Local", desc: "Relaciones y entendimiento de utilities, mineras, desarrolladores, operadores telecom, proveedores y actores de infraestructura." },
        { title: "Integración Técnico-Comercial", desc: "Capacidad de conectar ingeniería, compras, bancabilidad y estrategia comercial en una lógica coherente de proyecto." },
        { title: "Interfaz Internacional", desc: "Socio local para fabricantes globales, inversionistas y empresas tecnológicas que ingresan a Chile y LATAM." },
        { title: "Mentalidad de Ejecución", desc: "Nos enfocamos en acción estructurada, alineación de stakeholders y preparación de proyectos." },
        { title: "Agnósticos en Tecnología", desc: "Recomendamos lo que se ajusta al proyecto, no a una relación de marca." },
        { title: "Foco en Bancabilidad", desc: "Ayudamos a que los proyectos sean entendibles y accionables para capital, clientes y socios estratégicos." },
      ],
    },
    contact: {
      overline: "Contacto",
      headline: "Conversemos tu proyecto de infraestructura energética",
      intro: "Ya sea que estés desarrollando un proyecto BESS, entrando a Chile o LATAM con tecnología, evaluando una necesidad energética minera o industrial, o estructurando infraestructura digital power, GJC puede ayudar a definir el próximo paso.",
      form: {
        name: "Nombre Completo",
        company: "Empresa",
        email: "Email",
        interest: "Área de Interés",
        interestOptions: ["Integración BESS", "Infraestructura Eléctrica", "Energía Telecom", "Energía para IA & Data Centers", "Market Entry", "Advisory Estratégico", "Oportunidad de Alianza", "Otro"],
        message: "Mensaje",
        submit: "Enviar Mensaje",
        success: "Gracias. Nos pondremos en contacto pronto.",
      },
      whatsapp: "Chat por WhatsApp",
      email: "Envíenos un Email",
      emailAddress: "contacto@gjcenertec.com",
    },
    footer: {
      tagline: "Construyendo infraestructura energética bancable en Chile y LATAM.",
      copyright: "GJC Enertec Chile SpA. Todos los derechos reservados.",
      nav: {
        company: "Empresa",
        solutions: "Soluciones",
        connect: "Conectar",
      },
    },
  },
};

export default translations;
